import { Capacitor } from '@capacitor/core'
import { EventrDate, spLogTags } from '@eventr/common'
import {
    ColoursObj,
    FileWithStatus,
    Icon,
    MobileConfirmDialog,
    TDialogStore,
    Typography,
    useDialogStore,
} from '@eventr/common-storybook'
import { Avatar, Box, IconButton, Stack } from '@mui/material'
import { FC, useCallback, useRef } from 'react'
import { Control, Controller } from 'react-hook-form'
import { useShallow } from 'zustand/react/shallow'
import { FilePickerDialog } from '../../../common/FilePickerDialog'
import { UserAPI } from '../../../data/User'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { mapRoleNamesWithRole, openMobileMapsLink } from '../../../utils'

type AccountAvatarStackProps = {
    edit?: boolean
    profilePage?: boolean
    onChange?: (value: File) => void
    value?: string | File
    count?: number
}

export const AccountAvatarStack: FC<AccountAvatarStackProps> = ({
    edit = false,
    onChange = () => {},
    profilePage = false,
    value,
    count = 0,
}) => {
    const { orgConfig, wpUser, itineraryMap, profile, selectedItinID } =
        useBoundStore(
            useShallow((s) => {
                return {
                    orgConfig: s.orgConfig,
                    wpUser: s.wpUser,
                    itineraryMap: s.itineraryMap,
                    profile: s.profile,
                    selectedItinID: s.selectedItinID,
                }
            })
        )

    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            replaceDialog: s.replaceDialog,
            clearDialog: s.clearDialog,
        }))
    )

    const fileSelectRef = useRef<HTMLInputElement>(null)
    const cameraTakeRef = useRef<HTMLInputElement>(null)

    const onFilesChanged = (files: FileWithStatus[]) => {
        if (files.length > 0) {
            const newFile = files[files.length - 1].file
            if (newFile) {
                onChange(newFile)
            }
        }
    }

    function validLocation(lastLocation: any) {
        let dateShared
        try {
            dateShared = new EventrDate(
                Number(lastLocation.timeSharedUnix) * 1000
            )
        } catch (e) {
            spLogTags('Invalid date', ['ERROR'])
            spLogTags(e, ['ERROR'])
            return false
        }

        const map = itineraryMap.find(
            (itinMap) => Number(itinMap.id) === Number(selectedItinID)
        )

        function dateWithinRange(date: EventrDate, dateRange: EventrDate[]) {
            if (
                date.itinLocalLuxon.year < dateRange[0].itinLocalLuxon.year ||
                date.itinLocalLuxon.year > dateRange[1].itinLocalLuxon.year
            ) {
                return false
            } else if (
                (date.itinLocalLuxon.month <
                    dateRange[0].itinLocalLuxon.month &&
                    date.itinLocalLuxon.year <=
                        dateRange[0].itinLocalLuxon.year) ||
                (date.itinLocalLuxon.month >
                    dateRange[1].itinLocalLuxon.month &&
                    date.itinLocalLuxon.year >=
                        dateRange[1].itinLocalLuxon.year)
            ) {
                return false
            } else if (
                (date.itinLocalLuxon.day < dateRange[0].itinLocalLuxon.day &&
                    date.itinLocalLuxon.month <=
                        dateRange[0].itinLocalLuxon.month) ||
                (date.itinLocalLuxon.day > dateRange[1].itinLocalLuxon.day &&
                    date.itinLocalLuxon.month >=
                        dateRange[1].itinLocalLuxon.month)
            ) {
                return false
            } else {
                return true
            }
        }

        if (lastLocation.latitude && lastLocation.longitude && dateShared) {
            if (map) {
                const startDate = new EventrDate(map.start_time * 1000)
                const endDate = new EventrDate(map.end_time * 1000)
                if (dateWithinRange(dateShared, [startDate, endDate])) {
                    return true
                } else {
                    return false
                }
            }
        } else {
            return false
        }
    }

    function getLocationAge(lastLocation: any) {
        let timestamp
        try {
            timestamp = Number(lastLocation.timeSharedUnix)
        } catch (e) {
            spLogTags('Invalid timestamp', ['ERROR'])
            spLogTags(e, ['ERROR'])
            return false
        }

        const now = new EventrDate().getUnixMs / 1000
        if (now - timestamp <= 60) {
            return 'less than 1 minute ago'
        } else if (now - timestamp <= 3600) {
            return `${Math.floor((now - timestamp) / 60)} minutes ago`
        } else if (now - timestamp <= 86400) {
            return `${Math.floor((now - timestamp) / 3600)} hours ago`
        } else {
            return `${Math.floor((now - timestamp) / 86400)} days ago`
        }
    }

    const getProfilePicUrl = useCallback(() => {
        if (typeof value === 'string') {
            return value
        }
        if (value instanceof File) {
            return URL.createObjectURL(value)
        }
        return profile?.profilePicSizes?.medium?.url ?? profile?.profilePicUrl
    }, [profile?.profilePicUrl, profile?.profilePicSizes, value])

    const avatarClipPath = edit
        ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 0% 80%)'
        : 'none'

    const userProfileName = `${profile?.title ? profile?.title + ' ' : ''} ${
        profile?.firstName
    } ${profile?.lastName}`

    return (
        <>
            {profilePage ? (
                <>
                    <Stack alignItems="center" gap="16px" padding="16px 0px">
                        <Stack position="relative">
                            <Avatar
                                src={getProfilePicUrl()}
                                sx={{
                                    width: edit ? 132 : 172,
                                    height: edit ? 132 : 172,
                                    clipPath: avatarClipPath,
                                    border: 'solid 5px',
                                    borderColor: ColoursObj.font.primary,
                                }}
                            />
                            {edit && (
                                <>
                                    {/* Inputs are here and not in the dialog on purpose. they need to exist after the dialog is closed. carful of the close order. */}
                                    <input
                                        ref={fileSelectRef}
                                        type="file"
                                        style={{
                                            display: 'none',
                                        }}
                                        accept=".jpg, .jpeg, .png"
                                        onChange={(file) => {
                                            const selectedFile =
                                                file.target.files?.[0] ?? null
                                            onFilesChanged([
                                                {
                                                    file:
                                                        selectedFile ||
                                                        undefined,
                                                    status: 'uploaded',
                                                },
                                            ])
                                        }}
                                    />
                                    <input
                                        ref={cameraTakeRef}
                                        type="file"
                                        style={{
                                            display: 'none',
                                        }}
                                        accept="image/*"
                                        capture="user"
                                        onChange={(file) => {
                                            const selectedFile =
                                                file.target.files?.[0] ?? null
                                            onFilesChanged([
                                                {
                                                    file:
                                                        selectedFile ||
                                                        undefined,
                                                    status: 'uploaded',
                                                },
                                            ])
                                        }}
                                    />

                                    <IconButton
                                        component="label"
                                        style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            right: 10,
                                        }}
                                        onClick={() => {
                                            if (
                                                Capacitor.isNativePlatform() &&
                                                Capacitor.getPlatform() ===
                                                    'ios'
                                            ) {
                                                fileSelectRef.current?.click()
                                            } else {
                                                replaceDialog({
                                                    node: (
                                                        <FilePickerDialog
                                                            onFileSelected={(
                                                                file: File
                                                            ) => {
                                                                onFilesChanged([
                                                                    {
                                                                        file: file,
                                                                        status: 'uploaded',
                                                                    },
                                                                ])
                                                            }}
                                                        />
                                                    ),
                                                    backdropClosable: false,
                                                })
                                            }
                                        }}
                                    >
                                        <Icon
                                            name="Camera"
                                            bubble
                                            bubbleColour={
                                                ColoursObj.backgrounds.secondary
                                            }
                                            borderColour={
                                                ColoursObj.backgrounds.primary
                                            }
                                            iconColour={
                                                ColoursObj.shades.shade1
                                            }
                                            height="28px"
                                            width="28px"
                                        />
                                    </IconButton>
                                </>
                            )}
                        </Stack>
                        {!edit && (
                            <Stack alignItems="center">
                                <Typography
                                    variant="bodyBold"
                                    id="userNames"
                                    color={ColoursObj.core.primary}
                                >
                                    {userProfileName}
                                </Typography>
                                <Typography
                                    variant="bodySubtext"
                                    color={ColoursObj.core.primary}
                                    // id="role"
                                    id={wpUser?.roles
                                        ?.map((i) =>
                                            i === 'eventr_manager'
                                                ? '-adminRole dddd'
                                                : 'userRole'
                                        )
                                        .join('')}
                                >
                                    {wpUser?.roles &&
                                        wpUser.roles.map(
                                            (tag: string, index) => {
                                                return (
                                                    mapRoleNamesWithRole(
                                                        tag,
                                                        orgConfig?.availableRoles ??
                                                            []
                                                    ) +
                                                    (wpUser.roles.length ===
                                                    index + 1
                                                        ? ' '
                                                        : ', ')
                                                )
                                            }
                                        )}
                                </Typography>
                            </Stack>
                        )}
                        {orgConfig &&
                        !orgConfig?.disableLocationServices &&
                        !edit &&
                        wpUser &&
                        wpUser.lastLocation &&
                        validLocation(wpUser.lastLocation) ? (
                            <Stack>
                                <div
                                    onClick={() => {
                                        openMobileMapsLink(
                                            `${
                                                wpUser.lastLocation?.latitude ??
                                                0
                                            },${
                                                wpUser.lastLocation
                                                    ?.longitude ?? 0
                                            }`
                                        )
                                    }}
                                    style={{
                                        border: '2px solid #007AFF',
                                        height: '50px',
                                        borderRadius: '50px',
                                        width: '300px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-evenly',
                                    }}
                                >
                                    <Icon
                                        name="Location"
                                        iconColour="#007AFF"
                                    />
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Typography variant="bodySubtext">
                                            {i18n.t('location.location-shared')}
                                        </Typography>
                                        <Typography
                                            variant="bodySubtext"
                                            color="#758696"
                                        >
                                            {getLocationAge(
                                                wpUser.lastLocation
                                            )}
                                        </Typography>
                                    </div>
                                    <Typography
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            replaceDialog({
                                                node: (
                                                    <MobileConfirmDialog
                                                        text="Would you like to clear your shared location?"
                                                        header="Stop Sharing Location"
                                                        confirmText={i18n.t(
                                                            'general.continue'
                                                        )}
                                                        confirmBtnTextColour={
                                                            ColoursObj.core
                                                                .secondary
                                                        }
                                                        cancelText={i18n.t(
                                                            'general.cancel'
                                                        )}
                                                        onConfirm={async () => {
                                                            try {
                                                                clearDialog()
                                                                await UserAPI.updateUserMeta(
                                                                    {
                                                                        user_id:
                                                                            wpUser?.id.toString(),
                                                                        user_meta:
                                                                            {
                                                                                lastLocation:
                                                                                    null,
                                                                            },
                                                                    }
                                                                ).then(
                                                                    (resp) => {
                                                                        spLogTags(
                                                                            resp,
                                                                            [
                                                                                'location',
                                                                            ]
                                                                        )
                                                                        bs().fetchUsers()
                                                                    }
                                                                )
                                                            } catch (e) {
                                                                if (
                                                                    (e instanceof
                                                                        Error &&
                                                                        (
                                                                            e as any
                                                                        )
                                                                            .code ===
                                                                            'ERR_NETWORK') ||
                                                                    (e instanceof
                                                                        Error &&
                                                                        e.message ===
                                                                            'Network Error')
                                                                ) {
                                                                    defaultAddSnack(
                                                                        i18n.t(
                                                                            'snack.offline-stop-sharing'
                                                                        ),
                                                                        'error'
                                                                    )
                                                                }
                                                                spLogTags(e, [
                                                                    'ERROR',
                                                                ])
                                                            }
                                                        }}
                                                        onCancel={clearDialog}
                                                    />
                                                ),
                                                backdropClosable: false,
                                            })
                                        }}
                                        color={ColoursObj.states.error}
                                    >
                                        {i18n.t('location.stop-sharing')}
                                    </Typography>
                                </div>
                            </Stack>
                        ) : (
                            <Stack />
                        )}
                    </Stack>
                </>
            ) : (
                <>
                    <Box
                        display="grid"
                        gridTemplateColumns="auto auto 1fr"
                        alignItems="center"
                        justifyItems="end"
                        gap="16px"
                        borderBottom={`1px solid ${ColoursObj.shades.shade4}`}
                        padding="16px 0px"
                        onClick={() => {
                            window.pushPath('/account/profile')
                        }}
                    >
                        <Stack>
                            {!!count && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        display: 'grid',
                                        backgroundColor:
                                            ColoursObj.states.error,
                                        borderRadius: '50%',
                                        height: '16px',
                                        width: '16px',
                                        justifyItems: 'center',
                                        alignItems: 'center',
                                        translate: '47px -8px',
                                    }}
                                >
                                    <Typography
                                        color={ColoursObj.shades.shade5}
                                        variant="bodySubtext"
                                        fontSize={count > 9 ? '8px' : '12px'}
                                        lineHeight={count > 9 ? '8px' : '12px'}
                                    >
                                        {count}
                                    </Typography>
                                </Box>
                            )}
                            <Avatar
                                src={getProfilePicUrl()}
                                sx={{ width: 56, height: 56 }}
                            />
                        </Stack>
                        <Stack>
                            <Typography
                                variant="bodyBold"
                                id="userNames"
                                color={ColoursObj.core.primary}
                            >
                                {userProfileName}
                            </Typography>
                            <Typography
                                color={ColoursObj.core.primary}
                                variant="bodySubtext"
                                id="userRole"
                            >
                                {wpUser?.roles &&
                                    wpUser?.roles?.map((tag: string, index) => {
                                        return (
                                            mapRoleNamesWithRole(
                                                tag,
                                                orgConfig?.availableRoles ?? []
                                            ) +
                                            (wpUser.roles.length === index + 1
                                                ? ' '
                                                : ', ')
                                        )
                                    })}
                            </Typography>
                        </Stack>
                        <Box display="grid">
                            <Icon name="Chevron_Right" />
                        </Box>
                    </Box>
                </>
            )}
        </>
    )
}

export type FormAccountAvatarStackProps = AccountAvatarStackProps & {
    control: Control
    name: string
}

export const FormAccountAvatarStack: React.FC<FormAccountAvatarStackProps> = (
    props
) => {
    return (
        <Controller
            name={props.name}
            control={props.control}
            render={({ field: { onChange, value } }) => {
                return (
                    <AccountAvatarStack
                        {...props}
                        onChange={onChange}
                        value={value}
                    />
                )
            }}
        />
    )
}
